import DatePicker from '@data/models/DatePicker';
import TimeUtils from '@business/utils/time';
import { Box } from '@chakra-ui/react';
import { useStore } from '@data/other/withStore';
import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Month from './Month';
import { Routes as AppRoutes } from '@view/routes';
import { useNavigate } from '@view/hooks/useNavigate';
import BEIcon from '@molecules/BEIcon';
import { useParams } from 'react-router-dom';
import { Day, calculate } from '@calendar';
import { observer } from 'mobx-react';

const today = TimeUtils.getToday();

const StyledCalendar = styled.div<{ isMobile?: boolean }>`
    display: flex;
    align-items: center;
    flex-direction: column;
    .month-container {
        position: relative;
        display: flex;
        gap: 24px;
        min-height: 400px;

        ${({ isMobile }) => {
          if (isMobile) {
            return `
                    flex-direction: column;
                `;
          }
        }}
    }
`;

const StyleNavButtons = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    justify-content: space-between;
    width: 100%;

    .nav-buttom {
        width: 40px;
        height: 40px;
        background: #FFFFFF;
        border: 1px solid #99A1AF;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }
`;

interface CalendarProps {
  step?: 'checkin' | 'checkout';
  isMobile?: boolean;
  checkin?: Date;
  checkout?: Date;
  onSelectDate?: (value: DatePicker) => void;
  searchOnSelect?: boolean;
}

const Calendar = ({
  isMobile,
  checkin,
  checkout,
  onSelectDate,
  searchOnSelect,
  ...props
}: CalendarProps) => {
  const [date, setDate] = useState<Date>(checkin || new Date());
  const [step, setStep] = useState<'checkin' | 'checkout'>(
    props.step || 'checkin'
  );
  const { property, components } = useStore();
  const { accommodations, availabilityCalendar } = property;

  const navigate = useNavigate();
  const { propertyCode } = useParams();

  const [dates, setDates] = useState<DatePicker>({
    checkin,
    checkout,
  });

  useEffect(() => {
    if (props.step && props.step !== step) {
      setStep(props.step);
      if (step !== 'checkout' && props.step === 'checkin') {
        setDates({ checkin: undefined, checkout: undefined });
      }
    }
  }, [props.step, setDates, step]);

  const months = useMemo(() => {
    const months: Date[] = [];
    const size = isMobile ? 6 : 2;
    for (let i = 0; i < size; i++) {
      months.push(TimeUtils.getDateOffset(0, i, 0, date));
    }
    return months;
  }, [date, isMobile]);

  const days: undefined | Day[] = useMemo(() => {
    if (!availabilityCalendar.model) return undefined;
    const checkin: string | undefined = dates.checkin
      ? TimeUtils.format(dates.checkin)
      : undefined;
    return calculate(availabilityCalendar.model.data, checkin);
  }, [availabilityCalendar.model, dates.checkin]);

  const search = useCallback(
    async (d: DatePicker) => {
      if (!d.checkin || !d.checkout) return;
      await new Promise((res) => setTimeout(res, 250));
      components.promoCode.set(d);
      await accommodations.search(d.checkin, d.checkout);
      navigate(`/${propertyCode}/${AppRoutes.Accommodations}`);
    },
    [components.promoCode, accommodations, navigate, propertyCode]
  );

  const updateSelections = useCallback(
    (d: DatePicker) => {
      setDates(d);
      onSelectDate?.(d);
      if (searchOnSelect && d.checkin && d.checkout) search(d);
    },
    [searchOnSelect, onSelectDate, search]
  );

  const onClickDate = useCallback(
    (date: Date) => {
      if (dates.checkin && TimeUtils.isEqual(date, dates.checkin)) {
        setStep('checkin');
        updateSelections({ checkin: undefined, checkout: undefined });
        return;
      } else if (dates.checkout && TimeUtils.isEqual(date, dates.checkout)) {
        updateSelections({ ...dates, checkout: undefined });
        return;
      } else if (step === 'checkin') {
        setStep('checkout');
        updateSelections({ checkin: date, checkout: undefined });
      } else if (step === 'checkout') {
        if (!dates.checkin) {
          setStep('checkin');
          return;
        }
        const dateOffset = TimeUtils.getDiff(date, dates.checkin);
        if (dateOffset > 0) {
          updateSelections({ ...dates, checkout: date });
        }
      }
    },
    [dates, step, setStep, updateSelections]
  );

  const onClickMove = useCallback(
    (direction: -1 | 1) => {
      const nextDate: Date = TimeUtils.getDateOffset(0, direction, 0, date);
      const monthOffset = TimeUtils.getMonthDiff(nextDate, today);
      if (monthOffset >= 0) setDate(nextDate);
    },
    [date, setDate]
  );

  return (
    <StyledCalendar isMobile={isMobile}>
      <Box className="month-container">
        {!isMobile && (
          <StyleNavButtons>
            <div
              className={'nav-buttom nav-buttom-left'}
              onClick={() => onClickMove(-1)}
            >
              <BEIcon name={'arrow-left'} />
            </div>
            <div
              className="nav-buttom nav-buttom-right"
              onClick={() => onClickMove(1)}
            >
              <BEIcon name={'arrow-right'} />
            </div>
          </StyleNavButtons>
        )}
        {months.map((month, index) => (
          <Month
            key={index}
            date={month}
            checkin={dates.checkin}
            checkout={dates.checkout}
            days={days}
            onClickDate={onClickDate}
          />
        ))}
      </Box>
    </StyledCalendar>
  );
};

export default observer(Calendar);
